import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './lightbox.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import type { LightboxAsset, LightboxImageFitType, LightboxMediaItem } from './interfaces.js'
import {
  mapLightboxAssets,
  mapPdpProductToLightboxMediaItems,
  resolveVimeoThumbnails,
} from './lightbox-assets.js'
import './components/lightbox-thumbnails/lightbox-thumbnails.js'
import './components/lightbox-modal/lightbox-modal.js'
import './components/lightbox-media/lightbox-media.js'
import { CSCLightboxModal } from './components/lightbox-modal/lightbox-modal.js'
import { createSwipeGesture } from './lightbox-swipe.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { PdpProduct } from 'components/discovery/pdp/typing/pdp-product.js'

export const lightboxHiddenFields = ['modal-title'] as const

export const iconButtonVariants = ['primary', 'secondary', 'ghost'] as const
export type IconButtonVariantType = typeof iconButtonVariants[number]

initializeIconsByRole({
  'csc-lightbox.previous': { name: 'arrow-left', style: 'light' },
  'csc-lightbox.next': { name: 'arrow-right', style: 'light' },
  'csc-lightbox.enlarge': { name: 'magnifying-glass-plus', style: 'solid' },
})

export const thumbnailPositions = ['bottom', 'left'] as const
export type ThumbnailPositionType = typeof thumbnailPositions[number]

export const badgePositions = ['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const
export type BadgePositionType = typeof badgePositions[number]

const jsonArrayConverter = {
  fromAttribute(value: string | null): unknown[] {
    if (value && value.length > 0) {
      return JSON.parse(decodeURI(value))
    }
    return []
  },
}

@customElement('csc-lightbox')
export class CSCLightbox extends HiddenFieldsMixin(LitElement, lightboxHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @property({
    type: Array,
    converter: jsonArrayConverter,
  })
  assets: LightboxAsset[] = []

  @property({ type: String, attribute: 'thumbnail-position' })
  thumbnailPosition: ThumbnailPositionType = 'bottom'

  @property({ type: String, attribute: 'badge-position' })
  badgePosition: BadgePositionType = 'top-left'

  @property({ type: String, attribute: 'icon-button-variant' })
  iconButtonVariant: IconButtonVariantType = 'primary'

  @property({ type: String, attribute: 'product-title' })
  productTitle = ''

  @property({ type: String, attribute: 'fallback-source' })
  fallbackSource = ''

  /** How the main image and the thumbnails fit their box, `contain` shows the whole image */
  @property({ type: String, attribute: 'image-fit' })
  imageFit: LightboxImageFitType = 'cover'

  @state()
  private mediaItems: LightboxMediaItem[] = []

  /** Product loaded by csc-pdp-provider, its media replace the `assets` attribute */
  @state()
  private pdpProduct: PdpProduct | null = null

  @state()
  private activeIndex = 0

  @state()
  private previousIndex: number | null = null

  @state()
  private slideDirection: 'left' | 'right' | null = null

  @query('csc-lightbox-modal')
  private modal!: CSCLightboxModal

  private animationTimeout: ReturnType<typeof setTimeout> | null = null

  private wasSwiped = false

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product()
      if (!product) return
      if (!this.productTitle) this.productTitle = product.name
      if (!this.fallbackSource) this.fallbackSource = product.default_picture?.large ?? ''
      this.pdpProduct = product
    })
  }

  private readonly swipeGesture = createSwipeGesture({
    onSwipeLeft: () => this.onSwipe(),
    onSwipeRight: () => this.onSwipe(-1),
  })

  private onSwipe(direction: 1 | -1 = 1) {
    this.wasSwiped = true
    if (direction === 1 && this.hasNext) {
      this.slideTo(this.activeIndex + 1)
    } else if (direction === -1 && this.hasPrevious) {
      this.slideTo(this.activeIndex - 1)
    }
  }

  private get activeItem(): LightboxMediaItem | undefined {
    return this.mediaItems[this.activeIndex]
  }

  private get hasPrevious(): boolean {
    return this.activeIndex > 0
  }

  private get hasNext(): boolean {
    return this.activeIndex < this.mediaItems.length - 1
  }

  override willUpdate(changedProperties: Map<string, unknown>) {
    super.willUpdate(changedProperties)
    if (changedProperties.has('assets') || changedProperties.has('pdpProduct') || changedProperties.has('fallbackSource')) {
      void this.syncMediaItems()
    }
  }

  private async syncMediaItems() {
    const items = this.pdpProduct
      ? mapPdpProductToLightboxMediaItems(this.pdpProduct, this.fallbackSource)
      : mapLightboxAssets(this.assets, this.fallbackSource)

    this.mediaItems = items

    if (this.activeIndex >= this.mediaItems.length) {
      this.activeIndex = Math.max(0, this.mediaItems.length - 1)
    }

    const withVimeoThumbnails = await resolveVimeoThumbnails(items)
    if (withVimeoThumbnails.some((item, index) => item.source !== items[index]?.source)) {
      this.mediaItems = withVimeoThumbnails
    }
  }

  private slideTo(newIndex: number) {
    if (newIndex === this.activeIndex || newIndex < 0 || newIndex >= this.mediaItems.length) return

    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout)
    }

    this.previousIndex = this.activeIndex
    this.slideDirection = newIndex > this.activeIndex ? 'left' : 'right'
    this.activeIndex = newIndex

    this.animationTimeout = setTimeout(() => {
      this.previousIndex = null
      this.slideDirection = null
      this.animationTimeout = null
    }, 350)

    this.dispatchSlideChange()
  }

  private onPreviousClick(event: Event) {
    event.stopPropagation()
    if (this.hasPrevious) {
      this.slideTo(this.activeIndex - 1)
    }
  }

  private onNextClick(event: Event) {
    event.stopPropagation()
    if (this.hasNext) {
      this.slideTo(this.activeIndex + 1)
    }
  }

  private onThumbnailClick(event: CustomEvent<{ index: number }>) {
    this.slideTo(event.detail.index)
  }

  private onMainImageClick() {
    if (this.wasSwiped) {
      this.wasSwiped = false
      return
    }
    this.modal?.open()
    this.dispatchEvent(new CustomEvent('lightbox-open', { bubbles: true, composed: true }))
  }

  private onModalClose() {
    this.dispatchEvent(new CustomEvent('lightbox-close', { bubbles: true, composed: true }))
  }

  private onModalSlideChange(event: CustomEvent<{ index: number }>) {
    this.activeIndex = event.detail.index
  }

  private dispatchSlideChange() {
    this.dispatchEvent(new CustomEvent('lightbox-slide-change', {
      detail: { index: this.activeIndex },
      bubbles: true,
      composed: true
    }))
  }

  private renderSlideMedia(item: LightboxMediaItem, className: string) {
    return html`
      <csc-lightbox-media
        class="${className}"
        .item="${item}"
        image-fit="${this.imageFit}"
      ></csc-lightbox-media>
    `
  }

  private renderControls() {
    if (this.mediaItems.length <= 1) return nothing

    return html`
      <div class="gallery-controls">
        ${this.hasPrevious ? html`
          <cse-icon-button
            class="control-previous"
            variant="${this.iconButtonVariant}"
            shadow
            @click="${this.onPreviousClick}"
            aria-label="${t('previous_label', { ns: 'csc-lightbox', default: 'Previous image' })}"
          >
            <cse-icon icon-role="csc-lightbox.previous" size="md"></cse-icon>
          </cse-icon-button>
        ` : html`<div></div>`}
        ${this.hasNext ? html`
          <cse-icon-button
            class="control-next"
            variant="${this.iconButtonVariant}"
            shadow
            @click="${this.onNextClick}"
            aria-label="${t('next_label', { ns: 'csc-lightbox', default: 'Next image' })}"
          >
            <cse-icon icon-role="csc-lightbox.next" size="md"></cse-icon>
          </cse-icon-button>
        ` : html`<div></div>`}
      </div>
    `
  }

  private renderTags() {
    return html`
      <div class="${classMap({
        'badge-group': true,
        [`position-${this.badgePosition}`]: true,
      })}"><slot name="badges">${this.renderPdpProductBadges()}</slot></div>
    `
  }

  private renderPdpProductBadges() {
    const tags = (this.pdpProduct?.tags ?? []).filter(tag => tag.as_badge)
    return tags.map(tag => html`<cse-badge variant="${tag.badge_type || 'default'}">${tag.name}</cse-badge>`)
  }

  override render() {
    if (this.mediaItems.length === 0) return nothing

    const activeItem = this.activeItem
    if (!activeItem) return nothing

    const previousItem =
      this.previousIndex !== null ? this.mediaItems[this.previousIndex] : undefined

    const isVideo = activeItem.kind === 'video'

    return html`
      <div
        class="${classMap({
          'csc-lightbox': true,
          [`position-${this.thumbnailPosition}`]: true,
        })}"
      >
        <div class="main-image-wrapper" @click="${this.onMainImageClick}">
          ${!isVideo ? this.renderTags() : nothing}
          <div
            class="${classMap({
              'main-image-container': true,
              'slide-left': this.slideDirection === 'left',
              'slide-right': this.slideDirection === 'right',
              'is-video': isVideo,
            })}"
            @touchstart="${this.swipeGesture.onTouchStart}"
            @touchmove="${this.swipeGesture.onTouchMove}"
            @touchend="${this.swipeGesture.onTouchEnd}"
          >
            ${previousItem ? this.renderSlideMedia(previousItem, 'image-exit') : nothing}
            ${this.renderSlideMedia(activeItem, this.slideDirection ? 'image-enter' : '')}
            ${isVideo ? html`<div class="video-click-overlay" aria-hidden="true"></div>` : nothing}
          </div>
          ${this.renderControls()}
          ${!isVideo ? html`
            <cse-chip class="enlarge-chip">
              <cse-icon icon-role="csc-lightbox.enlarge" size="md"></cse-icon>
              ${t('enlarge_text', { ns: 'csc-lightbox', default: 'Click to enlarge' })}
            </cse-chip>
          ` : nothing}
        </div>

        ${this.mediaItems.length > 1 ? html`
          <csc-lightbox-thumbnails
            .images="${this.mediaItems}"
            active-index="${this.activeIndex}"
            orientation="${this.thumbnailPosition === 'left' ? 'vertical' : 'horizontal'}"
            alignment="stretch"
            image-fit="${this.imageFit}"
            @thumbnail-click="${this.onThumbnailClick}"
          ></csc-lightbox-thumbnails>
        ` : nothing}

        <csc-lightbox-modal
          .images="${this.mediaItems}"
          active-index="${this.activeIndex}"
          product-title="${this.productTitle}"
          hidden-fields="${this.isHidden('modal-title') ? 'title' : ''}"
          @lightbox-modal-close="${this.onModalClose}"
          @lightbox-modal-slide-change="${this.onModalSlideChange}"
        ></csc-lightbox-modal>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-lightbox': CSCLightbox
  }
}
